/**
 * Agent Management API Service
 * 
 * Handles API calls for creating, updating, and managing human agents
 * for the live chat handoff system.
 */

import { api } from './api'
import { logger } from '../utils/logger'

/**
 * Agent interfaces
 */
export interface Agent {
  id: string
  name: string
  email: string
  role: 'agent' | 'lead' | 'supervisor' | 'admin'
  is_active: boolean
  is_online: boolean
  status: 'online' | 'away' | 'busy' | 'offline'
  status_message?: string
  max_concurrent_chats: number
  current_chat_count: number
  skills: string[]
  languages: string[]
  total_conversations: number
  avg_response_time_seconds: number
  avg_resolution_time_minutes: number
  customer_satisfaction_avg: number
  created_at: string
  last_seen: string
  team_id?: string
  team_name?: string
}

export interface CreateAgentRequest {
  name: string
  email: string
  role?: string
  max_concurrent_chats?: number
  skills?: string[]
  languages?: string[]
  team_id: string  // Required for team assignment
}

export interface UpdateAgentRequest {
  name?: string
  email?: string
  role?: string
  max_concurrent_chats?: number
  skills?: string[]
  languages?: string[]
  is_active?: boolean
}

export interface UpdateAgentStatusRequest {
  status: 'online' | 'away' | 'busy' | 'offline'
  status_message?: string
}

/**
 * Agent Management Service Class
 */
class AgentService {
  
  /**
   * Get all agents for the current user's organization
   */
  async getAgents(includeInactive: boolean = false): Promise<Agent[]> {
    try {
      logger.debug('Fetching agents', {
        service: 'AgentService',
        action: 'get_agents',
        includeInactive
      })

      const params = new URLSearchParams()
      if (includeInactive) {
        params.append('include_inactive', 'true')
      }

      const response = await api.get(`/agents?${params}`)
      
      logger.info('Agents fetched successfully', {
        service: 'AgentService',
        action: 'agents_fetched',
        count: response.data.length
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch agents', {
        service: 'AgentService',
        action: 'get_agents_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch agents')
    }
  }

  /**
   * Get detailed information about a specific agent
   */
  async getAgent(agentId: string): Promise<Agent> {
    try {
      logger.debug('Fetching agent detail', {
        service: 'AgentService',
        action: 'get_agent',
        agentId
      })

      const response = await api.get(`/agents/${agentId}`)
      
      logger.info('Agent detail fetched successfully', {
        service: 'AgentService',
        action: 'agent_detail_fetched',
        agentId
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch agent detail', {
        service: 'AgentService',
        action: 'get_agent_error',
        agentId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch agent detail')
    }
  }

  /**
   * Create a new agent
   */
  async createAgent(agentData: CreateAgentRequest): Promise<Agent> {
    try {
      logger.debug('Creating new agent', {
        service: 'AgentService',
        action: 'create_agent',
        data: { name: agentData.name, email: agentData.email, role: agentData.role }
      })

      const response = await api.post('/agents', agentData)
      
      logger.info('Agent created successfully', {
        service: 'AgentService',
        action: 'agent_created',
        agentId: response.data.id,
        name: response.data.name
      })

      return response.data
    } catch (error: any) {
      logger.error('Failed to create agent', {
        service: 'AgentService',
        action: 'create_agent_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      
      // Extract meaningful error message from API response
      const data = (error as any)?.data
      if (data?.code === 'limit_reached' && data?.metric === 'team_members') {
        const limit = data?.limit ?? ''
        throw new Error(`Team members limit reached (${limit}). Upgrade your plan to add more agents.`)
      }
      if (error instanceof Error && error.message.includes('already exists')) {
        throw new Error('An agent with this email already exists')
      }
      throw new Error((error as any)?.message || 'Failed to create agent')
    }
  }

  /**
   * Update an existing agent
   */
  async updateAgent(agentId: string, agentData: UpdateAgentRequest): Promise<Agent> {
    try {
      logger.debug('Updating agent', {
        service: 'AgentService',
        action: 'update_agent',
        agentId,
        data: agentData
      })

      const response = await api.put(`/agents/${agentId}`, agentData)
      
      logger.info('Agent updated successfully', {
        service: 'AgentService',
        action: 'agent_updated',
        agentId
      })

      return response.data
    } catch (error) {
      logger.error('Failed to update agent', {
        service: 'AgentService',
        action: 'update_agent_error',
        agentId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to update agent')
    }
  }

  /**
   * Delete (deactivate) an agent
   */
  async deleteAgent(agentId: string): Promise<void> {
    try {
      logger.debug('Deleting agent', {
        service: 'AgentService',
        action: 'delete_agent',
        agentId
      })

      await api.delete(`/agents/${agentId}`)
      
      logger.info('Agent deleted successfully', {
        service: 'AgentService',
        action: 'agent_deleted',
        agentId
      })
    } catch (error) {
      logger.error('Failed to delete agent', {
        service: 'AgentService',
        action: 'delete_agent_error',
        agentId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to delete agent')
    }
  }

  /**
   * Update agent status and availability
   */
  async updateAgentStatus(
    agentId: string, 
    statusData: UpdateAgentStatusRequest
  ): Promise<Agent> {
    try {
      logger.debug('Updating agent status', {
        service: 'AgentService',
        action: 'update_agent_status',
        agentId,
        status: statusData.status
      })

      const response = await api.put(`/agents/${agentId}/status`, statusData)
      
      logger.info('Agent status updated successfully', {
        service: 'AgentService',
        action: 'agent_status_updated',
        agentId,
        status: statusData.status
      })

      return response.data
    } catch (error) {
      logger.error('Failed to update agent status', {
        service: 'AgentService',
        action: 'update_agent_status_error',
        agentId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to update agent status')
    }
  }
}

// Create and export service instance
export const agentService = new AgentService()

// Export service class for testing
export { AgentService }
